/**
 * File: apps/qbench-cp/reactapp/src/shared/constants/misc.js
 *
 * Purpose: Collects miscellaneous constants that don't belong to a more specific constants file
 *     — pagination, onboarding, quotation/job/export states, licensing keys, and color
 *     defaults.
 *
 * Scope: Cross-cutting constants used by pagination, product tours, quotations, background job
 *     tracking, exports, spreadsheet/rich-text libraries, and default report colors in the
 *     customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Exports pagination and onboarding-target sizing constants, `PRODUCT_TOURS` and
 *     `PORTAL_ADMIN_ONBOARDING_TARGET_CLASSES` (the latter generated programmatically as CSS
 *     class selectors), quotation state enums and a `QUOTATION_DISPLAY_FILTERS` list describing
 *     preset filter buttons, `JOB_STATE` and `EXPORT_STATE` enums for async task tracking, license
 *     keys for Handsontable/jspreadsheet and API keys for TinyMCE, a `COLOR_MAP` of default report
 *     theming colors, a `REPORT_STATE` enum, and default date/time format strings.
 *
 * Exports: DEFAULT_PAGE_SIZE, NUM_PORTAL_ADMIN_ONBOARDING_TARGETS, PRODUCT_TOURS,
 *     PORTAL_ADMIN_ONBOARDING_TARGET_CLASSES, QUOTATION_STATE, QUOTATION_PENDING_STATE,
 *     QUOTATION_DISPLAY_FILTERS, JOB_STATE, EXPORT_STATE, HANDSONTABLE_LICENSE_KEY,
 *     JSPREADSHEET_LICENSE_KEY, TINYMCE_API_KEY, TINYMCE_API_KEY_FALLBACK, COLOR_MAP,
 *     REPORT_STATE, DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT.
 */

export const DEFAULT_PAGE_SIZE = 20;
export const NUM_PORTAL_ADMIN_ONBOARDING_TARGETS = 4;

export const PRODUCT_TOURS = {
  SAMPLES_BULK_UPLOAD: 'SAMPLES_BULK_UPLOAD',
  PORTAL_ADMIN_ONBOARDING: 'PORTAL_ADMIN_ONBOARDING',
};

export const PORTAL_ADMIN_ONBOARDING_TARGET_CLASSES = Object.fromEntries(
  Array(NUM_PORTAL_ADMIN_ONBOARDING_TARGETS)
    .fill()
    .map((_, i) => {
      const idx = i + 1;
      return [`TARGET_${idx}`, `.onboarding-target-${idx}`];
    }),
);

export const QUOTATION_STATE = {
  SENT: 'SENT',
  VIEWED: 'VIEWED',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
};

export const QUOTATION_PENDING_STATE = [
  QUOTATION_STATE.SENT,
  QUOTATION_STATE.VIEWED,
];

export const QUOTATION_DISPLAY_FILTERS = [
  {
    name: 'Accepted & Active',
    params: {
      statuses: QUOTATION_STATE.ACCEPTED,
      expired: false,
    },
  },
  {
    name: 'Pending Acceptance',
    params: {
      statuses: QUOTATION_PENDING_STATE,
    },
  },
  {
    name: 'Accepted & Expired',
    params: {
      statuses: QUOTATION_STATE.ACCEPTED,
      expired: true,
    },
  },
];

export const JOB_STATE = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
};

export const EXPORT_STATE = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  DOWNLOADED: 'DOWNLOADED',
  SUCCESS: 'EXPORT_SUCCESS',
  FAILED: 'EXPORT_FAILED',
};

export const HANDSONTABLE_LICENSE_KEY =
  'YWQ1NTUtZjQ0ZDAtMmM1MDUtMzM2NDEtNWYxNGQ=';

export const JSPREADSHEET_LICENSE_KEY =
  'TldKaVkyTmtOMkkyTmpnNU9EWTROakkxTldJMk9ESXhZMlkxWmpnMVpXUXhOMlExTVdObU0yUmtPREU0TWpNMllqUXpORE00TlRWaU1EZzBNemsxTm1FNVlqZGxNRE5rWm1ZMk5UTXdNR1UzTWpObU9XSXpZekptWVRoak9EZG1ZakppTmpjelpqWTFNVEJoTnpFNE9UaGxNakV4TlRrMllqVXdaRE00TXpVc1pYbEthbUpIYkd4aWJsSktXa05KTmtscVpHdGFWRVY0V21wbk1FNXRVbXhPVkdSdFRWUk9iVTFYU21sUFJGVXpUbTFhYlZsWFNtMU9SMFUwV1ZSV2FVNHlUbWxPUkdOcFRFTktkVmxYTVd4SmFtOXBWVlZLYkdKdFRtOUpSV3gxV1hrMGFVeERTbXRaV0ZKc1NXcHZlRTU2WjNwTlJFMTZUV3BCZDB4RFNtdGlNakZvWVZjMGFVOXNjMmxqVjBwc1ltMU9iMHh0Tld4a1EwbHpTV3BGZVU1NU5IZE1ha0YxVFZOSmMwbHVaR3haYVVselNXMTRkbGt5Um5OaFJ6bDZaRU5LWkV4RFNuZGlSMFoxU1dwdmFVMTZVV2xNUTBwNldUSTVkMXBUU1RaWGVVb3lUbmxKYzBsdVdUUkphWGRwWkdwcmFVeERTakpOVkVGcFRFTktNazFVUldsTVEwcHRZak5LZEdSWGVHaEphWGRwV20wNWVXSllUV2xNUTBwNVdsYzFhMXBZU1dsTVEwcDNXVmhLZWxwWVNXbE1RMHB3WWxoQ2RtTnVVbXhqYVVselNXNWFhR0pIYkd0WldGSndZakkxZWtscGQybFpNamwwWWxkV2RXUklUV2xNUTBwNldsZEdlVmt5WjJsTVEwcHFZVWRHZVdSSVRXbE1RMHBwV1ZoSmFVeERTbnBoUjFac1pFaE5hVXhEU25waFIwWjNXbGhOYVV4RFNucGFXRW95V2xoSmFVeERTbTFpTTBwMFdWaFJhVXhEU25kamJXeDFaRU5LWkdaUlBUMD0=';

export const TINYMCE_API_KEY =
  'NXZpdDdsemVhN2Q1OWo2d2Jsemllbjdjamtwa2VlcXNuczlkMTl4NWdmd3N4MGY0';
export const TINYMCE_API_KEY_FALLBACK =
  'cWFnZmZyM3BrdXYxN2E4b24xYWZheDY2MWlyc3QxaGJyNGU2dGJ2ODg4c3o5MWpj';

export const COLOR_MAP = {
  header_bg_color: '#f9f9f9',
  header_font_color: '#337ab7',
  card_accent_color: '#24324c',
  card_accent_font_1_color: '#d4edff',
  card_accent_font_2_color: '#92d1ff99',
  link_color: '#337ab7',
  body_bg_color: '#f0f8ff',
  test_name_bg_color: '#f7fbff',
  gradient_bg_color_1: '#2a86c7',
  gradient_bg_color_2: '#2f6ab4',
  gradient_font_color: '#ffffff',
  button_bg_color: '#337ab7',
  button_font_color: '#f9f9f9',
};

export const REPORT_STATE = {
  PUBLISHED: 'PUBLISHED',
  OVERRIDDEN: 'OVERRIDDEN',
};

export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
export const DEFAULT_TIME_FORMAT = 'hh:mm A';
